import { nullable, z } from 'zod';

const RoleDataSchema = z.object({
  activeResourcesCount: z.number(),
  openSlotsCount: z.number(),
  additionalResourcesCount: z.number(),
});

const CapacityDashboardResource_ResponseSchema = z.object({
  id: z.string(),
  externalResourceId: z.string(),
  name: z.string(),
  openSlotsVirtualCount: z.number(),
  openSlotsClaimOwnerCount: z.number(),
  openSlotsFieldCount: z.number(),
});

const CapacityDashboardLocation_ResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  resources: z.array(CapacityDashboardResource_ResponseSchema),
  fieldData: RoleDataSchema,
  virtualData: RoleDataSchema,
  claimOwnerData: RoleDataSchema,
});

const CapacityDashboardData_ResponseSchema = z.object({
  id: z.string(),
  date: z.string(),
  locations: z.array(CapacityDashboardLocation_ResponseSchema),
  fieldData: RoleDataSchema,
  virtualData: RoleDataSchema,
  claimOwnerData: RoleDataSchema,
});

const CapacityDashboard_ResponseSchema = z.object({
  query: z.string(),
  totalFieldData: RoleDataSchema,
  totalVirtualData: RoleDataSchema,
  totalClaimOwnerData: RoleDataSchema,
  data: z.array(CapacityDashboardData_ResponseSchema),
});

const CapacityDashboardLocation_RequestSchema = z.union([
  z.object({
    type: z.literal('state'),
    state: z.string(),
    unassignedTasks: z.number().default(0),
  }),
  z.object({
    type: z.literal('county'),
    state: z.string(),
    county: z.string(),
    unassignedTasks: z.number().default(0),
  }),
  z.object({
    type: z.literal('zip'),
    zip: z.string(),
    unassignedTasks: z.number().default(0),
  }),
]);

const CapacityTypes = z.enum(['ClaimOwner', 'Field', 'Virtual']);
const COLocationTypes = z.enum(['Field', 'Virtual']).optional();

const AssignmentTypes = z
  .union([
    z.enum(['Initial', 'Supplement', 'Catastrophe', 'Non-Catastrophe']),
    z.string().length(0),
    z.null(),
  ])
  .nullable();

const ResourceCapacityPerDaySchema = z.object({
  field: z.number(),
  virtual: z.number(),
  claimOwner: z.number(),
});

const CapacityDashboard_RequestSchema = z.object({
  capacityType: CapacityTypes,
  perils: z.array(z.string()),
  roles: z.array(z.string()).optional(),
  teamIds: z.array(z.string()).optional(),
  startDate: z.string(),
  endDate: z.string(),
  durationInMinutes: z.number().default(0),
  driveTimeInMinutes: z.number().default(0),
  assignmentTypes: z.array(AssignmentTypes),
  resourceCapacityPerDay: ResourceCapacityPerDaySchema,
  resourceIds: z.array(z.string()),
  COLocationType: COLocationTypes.optional(),
  officeIds: z.array(z.string()).optional(),
  locations: z.array(CapacityDashboardLocation_RequestSchema).optional(),
  resourceAttributes: z
    .array(
      z.object({
        attributeDefinitionId: z.string(),
        valueIds: z.array(z.string()).nonempty(),
      })
    )
    .optional(),
});

// Infer types from schemas
export type RoleData = z.infer<typeof RoleDataSchema>;
export type CapacityDashboardResource_Response = z.infer<
  typeof CapacityDashboardResource_ResponseSchema
>;
export type CapacityDashboardLocation_Response = z.infer<
  typeof CapacityDashboardLocation_ResponseSchema
>;
export type CapacityDashboardData_Response = z.infer<
  typeof CapacityDashboardData_ResponseSchema
>;
export type CapacityDashboard_Response = z.infer<
  typeof CapacityDashboard_ResponseSchema
>;
export type CapacityDashboardLocation_Request = z.infer<
  typeof CapacityDashboardLocation_RequestSchema
>;
export type CapacityDashboard_Request = z.infer<
  typeof CapacityDashboard_RequestSchema
>;

export type CapacityDashboardFilters = {
  resourceIds: string[];
  capacityType: string;
  startDate: string;
  endDate: string;
  durationInMinutes: number;
  driveTimeInMinutes: number;
  county: { name: string; state: string }[];
  state: string[];
  zip: string[];
  catIndicator: 'Cat' | 'NonCat' | 'Both';
  perils: string[];
  severity: string;
  preferredLanguage: string;
  assignmentCategory: string;
};

export type Capacities = {
  capacityType: string;
  value: number;
};

export type CapacityDashboard = {
  locationCriteria: string;
  date: string;
  capacities: Capacities[];
};

export type CapacityData = {
  id: string;
  name: string;
  data: CapacityDashboard;
};

export type AdditionalResource = {
  date: string;
  count: number;
};

export type CapacityDashboardFormData = {
  parameters: {
    catIndicator: 'Cat' | 'NonCat' | 'Both';
    capacityType: string;
    location: string;
    //COLocationType: string;
    states: string[];
    county: { title: string; value: string }[];
    zipCodes: string[];
    assignedResourceId: string;
    perils: string[];
    roles: string[];
    assignmentTypes: string[];
  };
  timeFrame: {
    dateRange: [Date, Date];
  };
  assumption: {
    durationInMinutes: number;
    averageDriveTime: number;
    fieldResourcesCapacityPerDay: number;
    virtualResourcesCapacityPerDay: number;
    claimOwnerResourcesCapacityPerDay: number;
  };
};

// updated types to match new schema
const TeamSchema = z
  .object({
    id: z.string(),
    name: z.string(),
  })
  .nullable();

const ResourceOpenSlotsSchema = z
  .object({
    field: z.number(),
    claimOwner: z.number(),
    virtual: z.number(),
  })
  .nullable();

const ActiveResourcesSchema = z
  .object({
    fieldByDate: z.number(),
    claimOwnerByDate: z.number(),
    virtualByDate: z.number(),
    fieldByLocation: z.number(),
    claimOwnerByLocation: z.number(),
    virtualByLocation: z.number(),
  })
  .nullable();

const AdditionalResourcesSchema = z
  .object({
    fieldByDate: z.number(),
    claimOwnerByDate: z.number(),
    virtualByDate: z.number(),
    fieldByLocation: z.number(),
    claimOwnerByLocation: z.number(),
    virtualByLocation: z.number(),
  })
  .nullable();

const OpenSlotsSchema = z
  .object({
    fieldByDate: z.number(),
    claimOwnerByDate: z.number(),
    virtualByDate: z.number(),
    fieldByLocation: z.number(),
    claimOwnerByLocation: z.number(),
    virtualByLocation: z.number(),
  })
  .nullable();

const ResourceSchema = z.object({
  id: z.string(),
  externalResourceId: z.string(),
  name: z.string(),
  // this is needed as if we use name the header gets hidden, don't know why will have to figure it our later
  resourceName: z.string().optional(),
  rowId: z.string(),
  date: z.string().nullable(),
  location: z.string().nullable(),
  COLocations: z.array(z.string()),
  team: TeamSchema,
  resourceClaimOwnerWeeklyCapacity: z.number().nullable(),
  resourceOpenSlots: ResourceOpenSlotsSchema,
  activeResources: ActiveResourcesSchema,
  additionalResources: AdditionalResourcesSchema,
  openSlots: OpenSlotsSchema,
  dateData: z.array(
    z.object({
      date: z.string(),
      capacity: z.number(),
      totalTaskCount: z.number(),
      hoverData: z.nullable(z.any()),
    })
  ),
});

const TotalsSchema = z
  .object({
    field: z.object({
      openSlotsCount: z.number(),
      additionalResourceCount: z.number(),
    }),
    virtual: z.object({
      openSlotsCount: z.number(),
      additionalResourceCount: z.number(),
    }),
    claimOwner: z.object({
      openSlotsCount: z.number(),
      additionalResourceCount: z.number(),
    }),
  })
  .nullable();

export const CapacityResponseSchema = z.object({
  capacityType: z.string(),
  data: z.array(ResourceSchema),
  totals: TotalsSchema,
});

type CapacityDashboardApiResponseSchema = z.infer<
  typeof CapacityResponseSchema
>;

// Export the schemas
export {
  ResourceSchema,
  RoleDataSchema,
  CapacityDashboardResource_ResponseSchema,
  CapacityDashboardLocation_ResponseSchema,
  CapacityDashboardData_ResponseSchema,
  CapacityDashboard_ResponseSchema,
  CapacityDashboardLocation_RequestSchema,
  CapacityDashboard_RequestSchema,
  CapacityTypes,
  AssignmentTypes,
  CapacityDashboardApiResponseSchema,
};
